import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  Checkbox,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { ContractDeploymentRecord } from '@/types'
import {
  getDeploymentDisplayAddresses,
  type DeploymentDisplayAddressKind,
} from '@/utils/adminAddressPresentation'

interface DeploymentSettlementDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  cancelRef: React.RefObject<HTMLButtonElement | null>
  settlementTarget: ContractDeploymentRecord | null
  forceSettlement: boolean
  setForceSettlement: (value: boolean) => void
  forceReason: string
  setForceReason: (value: string) => void
  isSubmitting: boolean
}

const DeploymentSettlementDialog = ({
  isOpen,
  onClose,
  onConfirm,
  cancelRef,
  settlementTarget,
  forceSettlement,
  setForceSettlement,
  forceReason,
  setForceReason,
  isSubmitting,
}: DeploymentSettlementDialogProps) => {
  const { t } = useTranslation()
  const addressLabels: Record<DeploymentDisplayAddressKind, string> = {
    payment: t('contractRegistry.deployment.details.paymentAddress', 'Payment Address'),
    contract: t('contractRegistry.table.contractAddress', 'Contract Address'),
    predicted_contract: t('contractRegistry.deployment.details.predictedAddress'),
    deployed_contract: t('contractRegistry.deployment.details.deployedAddress'),
    vault_token_account: t(
      'contractRegistry.deployment.details.vaultTokenAccount',
      'Vault Token Account'
    ),
  }
  const addressRows = settlementTarget
    ? getDeploymentDisplayAddresses(settlementTarget)
    : []

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {t('contractRegistry.actions.triggerSettlement')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                {t('contractRegistry.actions.triggerSettlementConfirmation', {
                  orderId: settlementTarget?.order_id ?? '—',
                })}
              </Text>
              {addressRows.map(item => (
                <VStack key={item.kind} align="stretch" spacing={1}>
                  <Text fontSize="xs" color="gray.500">
                    {addressLabels[item.kind]}
                  </Text>
                  <Code fontSize="xs" wordBreak="break-all">
                    {item.address}
                  </Code>
                </VStack>
              ))}
              <Alert status="warning" variant="left-accent">
                <AlertIcon />
                <AlertDescription>
                  {t('contractRegistry.actions.triggerSettlementReminder')}
                </AlertDescription>
              </Alert>
              <FormControl>
                <Checkbox
                  id="force-settlement"
                  isChecked={forceSettlement}
                  onChange={event => {
                    setForceSettlement(event.target.checked)
                    if (!event.target.checked) {
                      setForceReason('')
                    }
                  }}
                >
                  {t('settlements.execute.fields.force.label')}
                </Checkbox>
                <FormHelperText>
                  {t('settlements.execute.fields.force.warning')}
                </FormHelperText>
              </FormControl>
              {forceSettlement && (
                <FormControl isRequired>
                  <FormLabel htmlFor="force-settlement-reason">
                    {t('settlements.execute.fields.force.reasonLabel')}
                  </FormLabel>
                  <Textarea
                    id="force-settlement-reason"
                    value={forceReason}
                    onChange={event => setForceReason(event.target.value)}
                    placeholder={t('settlements.execute.fields.force.reasonPlaceholder')}
                    rows={3}
                    maxLength={500}
                  />
                  <FormHelperText>
                    {t('settlements.execute.fields.force.reasonHelper')}
                  </FormHelperText>
                </FormControl>
              )}
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef as React.RefObject<HTMLButtonElement>} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={onConfirm}
              ml={3}
              isLoading={isSubmitting}
            >
              {t('settlements.execute.button.queueButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default DeploymentSettlementDialog
